import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EmailEditor, type EmailEditorRef } from '@react-email/editor';
import { StarterKit } from '@react-email/editor/extensions';
import { EmailTheming } from '@react-email/editor/plugins';
import { Placeholder } from '@tiptap/extension-placeholder';
import type { Editor, Extensions } from '@tiptap/core';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { EmailToolbar } from './EmailToolbar';
import { PlaceholderBar } from './PlaceholderBar';
import { StaticImageNode } from './nodes/static-image';
import type { EmailEditorPanelProps, EmailEditorValue } from './types';

// @react-email/editor@1.4.1 заявляет в exports сабпути link/button/image-bubble-menu.css,
// но физически в dist эти файлы отсутствуют (баг exports-карты пакета). Реально поставляются
// только эти три CSS — стили link/button/image-bubble-menu входят в общий bubble-menu.css.
import '@react-email/editor/themes/default.css';
import '@react-email/editor/styles/bubble-menu.css';
import '@react-email/editor/styles/slash-command.css';

const EXPORT_DEBOUNCE_MS = 350;

const EDITOR_PLACEHOLDER =
  'Начните писать письмо или наберите «/» для блоков...';

/**
 * Переиспользуемый визуальный редактор писем на @react-email/editor.
 *
 * Контракт с другими агентами:
 * - value?: EmailDoc — TipTap-документ (editor.getJSON())
 * - onChange?: ({ docJson, htmlTemplate }) — docJson для редактирования,
 *   htmlTemplate (ref.getEmail()) для Handlebars-рендера.
 *
 * Сам редактор @react-email/editor — WYSIWYG, отдельный inline-preview
 * не нужен. Предпросмотр письма с подстановкой переменных живёт в
 * resource email-templates (модалка через backend /preview).
 *
 * Тулбар и панель плейсхолдеров построены на shadcn-примитивах.
 * Bubble-menu / slash-command (кнопки, секции, колонки, картинки)
 * предоставляет сам @react-email/editor.
 */
export function EmailEditorPanel({
  value,
  onChange,
  placeholders = [],
  readOnly = false,
  theme = 'basic',
  className,
}: EmailEditorPanelProps) {
  const [editor, setEditor] = useState<Editor | null>(null);

  /**
   * @react-email/editor НЕ мёржит проп `extensions` со своим набором
   * по умолчанию — он его ПОЛНОСТЬЮ заменяет
   * (`extensionsProp ?? [StarterKit, Placeholder, EmailTheming]`,
   * см. dist/index.mjs). Поэтому, чтобы добавить кастомную image-ноду
   * и при этом не потерять StarterKit (doc/paragraph/text — без них
   * ProseMirror падает с «Schema is missing its top node type»),
   * мы воспроизводим набор пакета 1:1 и дописываем StaticImageNode.
   * theme/placeholder тоже задействованы только внутри дефолтного
   * массива пакета, поэтому прокидываем их здесь сами.
   */
  const extensions = useMemo<Extensions>(
    () => [
      StarterKit.configure(),
      Placeholder.configure({
        placeholder: EDITOR_PLACEHOLDER,
        includeChildren: true,
      }),
      EmailTheming.configure({ theme }),
      StaticImageNode,
    ],
    [theme],
  );

  const onChangeRef = useRef<EmailEditorPanelProps['onChange']>(onChange);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const exportFromRef = useCallback(async (ref: EmailEditorRef) => {
    const { html } = await ref.getEmail();
    const payload: EmailEditorValue = {
      docJson: ref.getJSON(),
      htmlTemplate: html,
    };
    onChangeRef.current?.(payload);
  }, []);

  const handleReady = useCallback(
    (ref: EmailEditorRef) => {
      setEditor(ref.editor);
      void exportFromRef(ref);
    },
    [exportFromRef],
  );

  const handleUpdate = useCallback(
    (ref: EmailEditorRef) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        void exportFromRef(ref);
      }, EXPORT_DEBOUNCE_MS);
    },
    [exportFromRef],
  );

  return (
    <Card className={cn('overflow-hidden p-0', className)}>
      <div className="flex min-h-[28rem] flex-col">
        <EmailToolbar editor={editor} disabled={readOnly} />
        <PlaceholderBar
          editor={editor}
          placeholders={placeholders}
          disabled={readOnly}
        />
        <div className="email-editor-surface flex-1 overflow-auto bg-background p-4">
          <EmailEditor
            content={value}
            extensions={extensions}
            theme={theme}
            editable={!readOnly}
            onReady={handleReady}
            onUpdate={handleUpdate}
          />
        </div>
      </div>
    </Card>
  );
}

export type { EmailDoc, EmailEditorValue, EmailEditorPanelProps } from './types';
